import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import {
  AlertComponent,
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  RowComponent,
  SpinnerModule,
  TableDirective
} from '@coreui/angular-pro';
import { CardAccountService } from 'credo';
import { TranslatePipe, TranslateService } from 'i18n';
import { AuthService } from 'auth';
import { CardAccount } from 'shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PageStep = 'loading' | 'no-cards' | 'already-active' | 'select' | 'confirm' | 'success' | 'error';

@Component({
  selector: 'app-beta-activation-page',
  standalone: true,
  imports: [
    CommonModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ButtonDirective,
    AlertComponent,
    BadgeComponent,
    SpinnerModule,
    TableDirective,
    TranslatePipe,
    RouterLink
  ],
  templateUrl: './beta-activation-page.component.html',
  styleUrls: ['./beta-activation-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BetaActivationPageComponent {
  private cardAccountService = inject(CardAccountService);
  private translateService = inject(TranslateService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  step = signal<PageStep>('loading');
  userCards = signal<CardAccount[]>([]);
  betaCard = signal<CardAccount | null>(null);
  selectedCardId = signal<number | null>(null);
  saving = signal(false);
  errorMessage = signal<string>('');

  lang = this.translateService.getActiveLang();

  selectedCard = computed(() => {
    const id = this.selectedCardId();
    return id ? this.userCards().find(c => c.id === id) : undefined;
  });

  constructor() {
    this.loadCards();
  }

  private loadCards(): void {
    this.step.set('loading');
    this.cardAccountService.getMyCardAccounts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.userCards.set(response.card_accounts ?? []);
          const existing = this.userCards().find(c => c.is_beta);
          if (existing) {
            this.betaCard.set(existing);
            this.step.set('already-active');
          } else if (this.userCards().length === 0) {
            this.step.set('no-cards');
          } else {
            this.step.set('select');
          }
        },
        error: () => {
          this.errorMessage.set(this.translateService.translate('beta_error_title'));
          this.step.set('error');
        }
      });
  }

  selectCard(id: number): void {
    this.selectedCardId.set(id);
  }

  confirmActivation(): void {
    if (!this.selectedCardId()) return;
    this.step.set('confirm');
  }

  activateBeta(): void {
    const cardId = this.selectedCardId();
    if (!cardId) return;

    this.saving.set(true);
    this.errorMessage.set('');

    this.cardAccountService.activateBeta(cardId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (card) => {
          this.saving.set(false);
          this.betaCard.set(card);
          this.step.set('success');
          this.authService.refreshUser()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
        },
        error: (err) => {
          this.saving.set(false);
          const errKey = err.error?.error;
          this.errorMessage.set(
            errKey === 'beta_limit_reached'
              ? this.translateService.translate('beta_limit_reached')
              : (errKey || this.translateService.translate('beta_error_title'))
          );
          this.step.set('error');
        }
      });
  }

  cancelConfirm(): void {
    this.step.set('select');
  }

  getCardTypeLabel(cardType: string): string {
    const labels: Record<string, string> = {
      silber: 'Silber',
      gold: 'Gold',
      platin: 'Platinum',
      m3: 'M3'
    };
    return labels[cardType] || cardType;
  }

  getCardTypeBadgeColor(cardType: string): string {
    const colors: Record<string, string> = {
      silber: 'secondary',
      gold: 'warning',
      platin: 'info',
      m3: 'primary'
    };
    return colors[cardType] || 'secondary';
  }
}